/**
 * Customer Mobile Home — v1.0 master design (matches approved dashboard mockup).
 *
 * Composition (top to bottom):
 *   1. Hero card: van photo + greeting + tagline
 *   2. Dual CTA cards: "Get a Quote" / "Track Transport"
 *   3. Live booking widget (or "Book your first trip" CTA if none active)
 *   4. Quick Access tiles grid (5 tiles)
 *   5. Trust banner: driver portrait + "Dedicated. Insured. Trusted."
 *
 * Used in place of the standard customer dashboard layout when running inside
 * the mobile shell. Falls through to existing booking list view via the
 * Bookings tab in the bottom nav.
 *
 * Props:
 *   profile     — { name, email, ... }
 *   bookings    — full array from /api/customer/bookings
 *   onRefresh   — pull-to-refresh handler (passthrough to existing load())
 */

import { Link } from "react-router-dom";
import {
  FileText, Navigation, Package, History, MapPin, CreditCard, HelpCircle,
  Shield, ChevronRight, Truck,
} from "lucide-react";

const ACTIVE_STATUSES = new Set(["awaiting_driver", "accepted", "in_progress"]);

function shortName(full = "") {
  return (full || "Customer").split(" ")[0];
}

function statusLabel(status_group) {
  switch (status_group) {
    case "awaiting_driver": return "Finding your driver";
    case "accepted":        return "Driver assigned";
    case "in_progress":     return "Driver en route";
    default:                return "In progress";
  }
}

export default function MobileCustomerHome({ profile, bookings = [] }) {
  const liveBooking = bookings.find(b => ACTIVE_STATUSES.has(b.status_group));
  return (
    <div className="t247-mob-home" data-testid="mobile-customer-home">
      {/* ----- 1. HERO ----- */}
      <div className="t247-hero">
        <img src="/brand/hero-van.png" alt="" className="t247-hero-img" />
        <div className="t247-hero-overlay" />
        <div className="t247-hero-content">
          <p className="t247-hero-eyebrow">
            <Truck size={14} /> SAME-DAY TRANSPORT YOU CAN TRUST
          </p>
          <h1 className="t247-hero-title">
            Hello <span className="t247-orange">{shortName(profile?.name)}</span>,
            <br />Let's get moving.
          </h1>
          <p className="t247-hero-sub">
            Dedicated, insured transport for anything, anywhere in the UK.
          </p>
        </div>
      </div>

      {/* ----- 2. DUAL CTA CARDS ----- */}
      <div className="t247-cta-row">
        <Link to="/" className="t247-cta-card t247-cta-primary" data-testid="cta-get-quote">
          <FileText size={28} strokeWidth={2.2} />
          <div>
            <div className="t247-cta-title">Get a Quote</div>
            <div className="t247-cta-sub">Instant price in 30 seconds</div>
          </div>
          <ChevronRight size={20} className="t247-cta-arrow" />
        </Link>
        <Link
          to={liveBooking ? `/customer/booking/${liveBooking.id}` : "/customer/dashboard?filter=active"}
          className="t247-cta-card t247-cta-secondary"
          data-testid="cta-track-transport"
        >
          <Navigation size={28} strokeWidth={2.2} />
          <div>
            <div className="t247-cta-title">Track Transport</div>
            <div className="t247-cta-sub">
              {liveBooking ? "Active booking" : "See your trips"}
            </div>
          </div>
          <ChevronRight size={20} className="t247-cta-arrow" />
        </Link>
      </div>

      {/* ----- 3. LIVE BOOKING WIDGET or first-trip CTA ----- */}
      {liveBooking ? (
        <Link
          to={`/customer/booking/${liveBooking.id}`}
          className="t247-live-card"
          data-testid="live-booking-card"
        >
          <div className="t247-live-map" aria-hidden>
            <div className="t247-live-map-pin t247-live-map-pin-pickup">
              <span /><MapPin size={14} />
            </div>
            <div className="t247-live-map-route" />
            <div className="t247-live-map-pin t247-live-map-pin-dropoff">
              <MapPin size={14} />
            </div>
          </div>
          <div className="t247-live-body">
            <div className="t247-live-eta-row">
              <span className="t247-live-eta-badge">
                {liveBooking.eta_minutes != null ? `ETA ${liveBooking.eta_minutes} min` : "Live"}
              </span>
              <span className="t247-live-ref">#{(liveBooking.id || "").slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="t247-live-route">
              <span className="t247-orange">{liveBooking.pickup_address || "Pickup"}</span>
              <span className="t247-live-arrow"> → </span>
              <span>{liveBooking.delivery_address || "Drop-off"}</span>
            </div>
            <div className="t247-live-status">
              {statusLabel(liveBooking.status_group)} · View details
              <ChevronRight size={16} />
            </div>
          </div>
        </Link>
      ) : (
        <Link to="/" className="t247-firsttrip-card" data-testid="first-trip-card">
          <div className="t247-firsttrip-icon"><Package size={28} /></div>
          <div className="t247-firsttrip-body">
            <div className="t247-firsttrip-title">Book your first trip</div>
            <div className="t247-firsttrip-sub">
              Compare instant quotes from vetted UK transport providers.
            </div>
          </div>
          <ChevronRight size={20} className="t247-cta-arrow" />
        </Link>
      )}

      {/* ----- 4. QUICK ACCESS ----- */}
      <div className="t247-section-head">QUICK ACCESS</div>
      <div className="t247-quick-grid">
        {[
          { icon: Package,    label: "Bookings", to: "/customer/dashboard?filter=active" },
          { icon: History,    label: "History",  to: "/customer/dashboard?filter=completed" },
          { icon: MapPin,     label: "Addresses",to: "/customer/dashboard#addresses" },
          { icon: CreditCard, label: "Payment",  to: "/customer/dashboard#payment" },
          { icon: HelpCircle, label: "Help",     to: "/support" },
        ].map(t => (
          <Link
            key={t.label}
            to={t.to}
            className="t247-quick-tile"
            data-testid={`quick-${t.label.toLowerCase()}`}
          >
            <t.icon size={22} />
            <span>{t.label}</span>
          </Link>
        ))}
      </div>

      {/* ----- 5. TRUST BANNER ----- */}
      <div className="t247-trust">
        <img
          src="/brand/driver-portrait.png"
          alt=""
          className="t247-trust-portrait"
        />
        <div className="t247-trust-body">
          <div className="t247-trust-title">
            <Shield size={16} /> DEDICATED. INSURED. TRUSTED.
          </div>
          <div className="t247-trust-sub">
            Every driver is vetted, insured to GBP 1M, and tracked in real-time. Your goods get the same care we'd give our own.
          </div>
        </div>
      </div>
    </div>
  );
}
